import { supabase } from '../../lib/supabase'
import type { Category, DayEntry } from '../../components/agent/scheduleDay'
import { periodDates, periodNumberFor, periodRangeLabel } from '../../lib/payPeriods'

// The agent's own real vocabulary (QUICK_STATUSES, scheduleDefaults.ts's
// WEEKLY_DEFAULT), a smaller surface than the supervisor board's full
// BOARD_CODES palette: work, off, leave, and training are the only
// categories a real submission ever carries, each with exactly one status
// code name except leave, whose own label already names the specific one
// (AgentCopySchedulePage.tsx's own QUICK_STATUSES sets label to the leave
// type's real name directly). Matches supabase/seed/01_organization.sql's
// status_codes.name and the shared edge-function validator
// (supabase/functions/_shared/scheduleSubmission.ts) exactly.
function statusCodeNameFor(entry: DayEntry): string | undefined {
  switch (entry.category as Category) {
    case 'work':
      return 'Scheduled workday'
    case 'off':
      return 'Regular day off'
    case 'leave':
      return entry.label
    case 'training':
      return 'Training'
    default:
      // 'detail', 'unavailable', and 'unscheduled' never come from the
      // agent's own quick-select (QUICK_STATUSES): a day with none of the
      // four real categories carries nothing to submit for, rather than
      // guessing at a status code no seeded catalog row actually has.
      return undefined
  }
}

// Only a work day carries an assignment, matching scheduleDefaults.ts's
// own WEEKLY_DEFAULT, which sets one for every work day and none for an
// off day.
function assignmentNameFor(entry: DayEntry): string | undefined {
  return entry.category === 'work' ? entry.detail : undefined
}

// "0700–1500" (SHIFT_TEMPLATE.time) becomes "07:00"/"15:00": the only
// place a start and end time exist in a DayEntry is this same prefix
// scheduleDay.ts's own scheduledHours reads, so a real work or training
// entry carries real times rather than leaving schedule_entries'
// scheduled_hours generated column at zero.
function timesFor(entry: DayEntry): { startTime?: string; endTime?: string } {
  const match = entry.label.match(/^(\d{2})(\d{2})–(\d{2})(\d{2})/)
  if (!match) return {}
  return { startTime: `${match[1]}:${match[2]}`, endTime: `${match[3]}:${match[4]}` }
}

// Shared by submitSchedule and reopenSchedule: the same day-to-entry
// conversion either edge function's own validator expects.
function entriesFor(days: DayEntry[]) {
  return days.flatMap((entry) => {
    const statusCode = statusCodeNameFor(entry)
    if (!statusCode) return []
    return [{ date: entry.date, statusCode, assignmentCode: assignmentNameFor(entry), ...timesFor(entry) }]
  })
}

// The outcome of a submit or a resubmit: whether the write landed and, when
// it did not, the reason the server gave, so the screen can say what to fix.
export type SubmitResult = { ok: true } | { ok: false; message: string | null }

async function outcome(error: unknown): Promise<SubmitResult> {
  if (!error) return { ok: true }
  const context = (error as { context?: { json?: () => Promise<{ error?: string }> } }).context
  try {
    const body = await context?.json?.()
    return { ok: false, message: body?.error ?? null }
  } catch {
    return { ok: false, message: null }
  }
}

// Returns whether the write actually landed, the same as
// leaveRequestStore.ts's own saveLeaveRequest: the caller needs to know
// before it confirms a submission a supervisor will never actually see.
export async function submitSchedule(periodStart: string, days: DayEntry[]): Promise<SubmitResult> {
  const { error } = await supabase.functions.invoke('v1-submit-schedule', {
    body: { periodStart, entries: entriesFor(days) },
  })
  return outcome(error)
}

// The reopen half of a returned submission: replaces its own
// schedule_entries and moves it back to submitted through
// v1-reopen-schedule-submission (supabase/migrations/20260922000000_schedule_submissions.sql's
// own muster.reopen_schedule_submission). Returns the same outcome
// submitSchedule's own return carries.
export async function reopenSchedule(scheduleSubmissionId: string, periodStart: string, days: DayEntry[]): Promise<SubmitResult> {
  const { error } = await supabase.functions.invoke('v1-reopen-schedule-submission', {
    body: { scheduleSubmissionId, periodStart, entries: entriesFor(days) },
  })
  return outcome(error)
}

// The first day of the agent's earliest active team placement, or null for
// an agent with none. A day before it is not part of their schedule.
export async function getMyPlacementStart(userId: string): Promise<string | null> {
  const employee = await supabase.from('employees').select('id').eq('profile_id', userId).maybeSingle()
  if (!employee.data) return null
  const { data } = await supabase
    .from('employee_assignments')
    .select('effective_from')
    .eq('employee_id', employee.data.id)
    .eq('active', true)
    .order('effective_from', { ascending: true })
    .limit(1)
  return (data as { effective_from: string }[] | null)?.[0]?.effective_from ?? null
}

// muster.schedule_submission_state (20260922000000_schedule_submissions.sql).
export type ScheduleSubmissionState = 'submitted' | 'approved' | 'returned' | 'denied'

export type MyScheduleSubmission = {
  id: string
  periodStart: string
  periodLabel: string
  dateRange: string
  state: ScheduleSubmissionState
  decisionComment?: string
  decidedByName?: string
}

type ScheduleSubmissionDecisionRow = { decision: string; comment: string | null; decided_at: string; decided_by_name: string }

type ScheduleSubmissionRow = {
  id: string
  period_start: string
  state: ScheduleSubmissionState
  decisions: ScheduleSubmissionDecisionRow[] | null
}

const SCHEDULE_SUBMISSION_COLUMNS = 'id, period_start, state, decisions(decision, comment, decided_at, decided_by_name)'

function toMyScheduleSubmission(row: ScheduleSubmissionRow): MyScheduleSubmission {
  // A reopened, resubmitted submission can gather a second decision, the
  // same reason leaveRequestStore.ts's own toLeaveRequest reads the
  // latest by decided_at rather than assuming PostgREST's own order.
  const latest = (row.decisions ?? []).reduce<ScheduleSubmissionDecisionRow | undefined>(
    (latest, current) => (!latest || current.decided_at > latest.decided_at ? current : latest),
    undefined,
  )
  // An approval a supervisor has since reversed no longer stands.
  const decision = latest?.decision === 'reverse' ? undefined : latest
  const periodNumber = periodNumberFor(row.period_start)
  return {
    id: row.id,
    periodStart: row.period_start,
    periodLabel: `Pay Period ${periodNumber}`,
    dateRange: periodRangeLabel(periodNumber),
    state: row.state,
    decisionComment: decision?.comment ?? undefined,
    decidedByName: decision?.decided_by_name,
  }
}

// The one real fact pendingDraftStore.ts's own sessionStorage copy cannot
// carry: once a supervisor decides a submission, this is what actually
// moved, and who said what, not the local draft, which only ever knows
// "submitted." Read unconditionally on the real employee_id, not gated on
// a local PendingDraft existing or matching its own period: sessionStorage
// does not survive a new device or a cleared browser, but the real record
// does, the same reason leaveRequestStore.ts's own getLeaveRequests never
// depends on anything local either. The most recently submitted one, since
// that is always the one still relevant to show on the dashboard; a fuller
// history that needs every period's own submission reads
// getMyScheduleSubmissions below instead. Null when the caller has no
// employee record, or has never submitted a real schedule.
export async function getMyLatestScheduleSubmission(userId: string): Promise<MyScheduleSubmission | null> {
  const { data: employee } = await supabase.from('employees').select('id').eq('profile_id', userId).maybeSingle()
  if (!employee) return null

  const { data } = await supabase
    .from('schedule_submissions')
    .select(SCHEDULE_SUBMISSION_COLUMNS)
    .eq('employee_id', employee.id)
    .order('period_start', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (!data) return null

  return toMyScheduleSubmission(data as unknown as ScheduleSubmissionRow)
}

// Every real schedule submission the agent has ever made, newest first:
// AgentRequestHistoryPage.tsx's own full history, unlike
// getMyLatestScheduleSubmission's single row, which is only ever enough
// for the dashboard's "what's current." A history view built from that
// one row alone would silently drop every earlier period's own decision,
// comment, and reopen the moment a newer period's own submission exists.
// Empty when the caller has no employee record, or has never submitted a
// real schedule.
export async function getMyScheduleSubmissions(userId: string): Promise<MyScheduleSubmission[]> {
  const { data: employee } = await supabase.from('employees').select('id').eq('profile_id', userId).maybeSingle()
  if (!employee) return []

  const { data } = await supabase
    .from('schedule_submissions')
    .select(SCHEDULE_SUBMISSION_COLUMNS)
    .eq('employee_id', employee.id)
    .order('period_start', { ascending: false })
  if (!data) return []

  return (data as unknown as ScheduleSubmissionRow[]).map(toMyScheduleSubmission)
}

// "07:00:00" (a Postgres `time`) becomes "0700": the reverse of
// timesFor's own regex above, matching AgentCopySchedulePage.tsx's own
// DayEntry.label convention for a work or training day (an "HHMM–HHMM"
// prefix, scheduleDay.ts's own scheduledHours reads it the same way).
function rawTimeLabel(startTime: string, endTime: string): string {
  const strip = (value: string) => value.slice(0, 5).replace(':', '')
  return `${strip(startTime)}–${strip(endTime)}`
}

type ScheduleEntryDayRow = {
  date: string
  start_time: string | null
  end_time: string | null
  status_codes: { name: string; category: Category } | null
  assignments: { name: string } | null
}

// entry.category/label/detail for one real schedule_entries row, the
// inverse of statusCodeNameFor/assignmentNameFor/timesFor above: those
// build a submit request from a DayEntry, this rebuilds a DayEntry from
// what was actually written. Mirrors
// src/pages/supervisor/approvedScheduleStore.ts's own dayFor exactly,
// kept apart rather than shared since that file reads across every
// employee on a team and this one reads a single agent's own submission.
function dayFromEntry(entry: ScheduleEntryDayRow): { category: Category; label: string; detail?: string } | null {
  const statusCode = entry.status_codes
  if (!statusCode) return null
  const hasTimes = Boolean(entry.start_time && entry.end_time)

  switch (statusCode.category) {
    case 'work':
      return {
        category: 'work',
        label: hasTimes ? rawTimeLabel(entry.start_time as string, entry.end_time as string) : statusCode.name,
        detail: entry.assignments?.name,
      }
    case 'training':
      return {
        category: 'training',
        label: hasTimes ? `${rawTimeLabel(entry.start_time as string, entry.end_time as string)} · ${statusCode.name}` : statusCode.name,
      }
    case 'leave':
      return { category: 'leave', label: statusCode.name, detail: 'Full day' }
    default:
      return { category: statusCode.category, label: statusCode.name }
  }
}

// A returned submission's own snapshot row (schedule_submissions.returned_entries,
// muster.decide_schedule_submission's own capture of the schedule_entries
// it deleted): raw ids rather than an embedded read's own nested
// objects, since a jsonb column carries no foreign keys for PostgREST to
// join through. Resolved against the same status_codes/assignments maps
// dayFromEntry already expects, so it reads exactly like a live
// schedule_entries row would.
type ReturnedEntrySnapshotRow = {
  date: string
  start_time: string | null
  end_time: string | null
  status_code_id: string
  assignment_id: string | null
}

// The day-by-day content behind a submission the agent can still edit, for
// AgentCopySchedulePage.tsx's own edit and resubmit path. A returned
// submission is read from its own returned_entries snapshot, not live
// schedule_entries: muster.decide_schedule_submission deletes those rows
// on return the same reason it always has on deny (coverage_base has no
// join back to a submission's own state, so a returned schedule cannot
// stay live there), capturing this snapshot first for exactly this
// reason. A submission still waiting for a decision keeps its live
// schedule_entries, which are read instead. Laid onto the period's own
// 14-day skeleton (periodDates) for the weekday/day/month fields a
// DayEntry needs that the entries themselves do not carry. A day the
// period skeleton has but the entries do not (never expected from a real
// submission, which always covers every day it holds) reads 'unscheduled'
// rather than silently dropping a day off the editor. Null if the
// submission does not belong to this account, has been decided, or
// somehow carries no content.
export async function getEditableScheduleSubmissionDays(userId: string, scheduleSubmissionId: string): Promise<DayEntry[] | null> {
  const { data: employee } = await supabase.from('employees').select('id').eq('profile_id', userId).maybeSingle()
  if (!employee) return null

  const { data: submission } = await supabase
    .from('schedule_submissions')
    .select('id, employee_id, period_start, state, returned_entries')
    .eq('id', scheduleSubmissionId)
    .eq('employee_id', employee.id)
    .in('state', ['returned', 'submitted'])
    .maybeSingle()
  if (!submission) return null
  if (submission.state === 'returned' && !submission.returned_entries) return null

  const [{ data: statusCodes, error: statusCodesError }, { data: assignments, error: assignmentsError }] = await Promise.all([
    supabase.from('status_codes').select('id, name, category'),
    supabase.from('assignments').select('id, name'),
  ])
  if (statusCodesError || assignmentsError) return null
  const statusCodeById = new Map((statusCodes ?? []).map((row) => [row.id as string, { name: row.name as string, category: row.category as Category }]))
  const assignmentById = new Map((assignments ?? []).map((row) => [row.id as string, { name: row.name as string }]))

  let entryRows: ScheduleEntryDayRow[]
  if (submission.state === 'submitted') {
    const periodEnd = periodDates(periodNumberFor(submission.period_start)).at(-1)?.date ?? submission.period_start
    const { data: live, error: liveError } = await supabase
      .from('schedule_entries')
      .select('date, start_time, end_time, status_codes(name, category), assignments(name)')
      .eq('employee_id', employee.id)
      .gte('date', submission.period_start)
      .lte('date', periodEnd)
    if (liveError) return null
    entryRows = (live ?? []) as unknown as ScheduleEntryDayRow[]
  } else {
    const snapshot = submission.returned_entries as unknown as ReturnedEntrySnapshotRow[]
    entryRows = snapshot.map((row) => ({
      date: row.date,
      start_time: row.start_time,
      end_time: row.end_time,
      status_codes: statusCodeById.get(row.status_code_id) ?? null,
      assignments: row.assignment_id ? (assignmentById.get(row.assignment_id) ?? null) : null,
    }))
  }
  const entryByDate = new Map(entryRows.map((row) => [row.date, row]))

  return periodDates(periodNumberFor(submission.period_start)).map((skeleton) => {
    const entry = entryByDate.get(skeleton.date)
    const day = entry && dayFromEntry(entry)
    return {
      ...skeleton,
      category: day?.category ?? 'unscheduled',
      label: day?.label ?? 'Unscheduled',
      detail: day?.detail,
      state: 'draft',
    }
  })
}
